import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../../lib/api';
import type { TreeSpecies } from '../../types';

const SpeciesList: React.FC = () => {
  const { data: species, isLoading, error } = useQuery({
    queryKey: queryKeys.treeSpecies,
    queryFn: api.getTreeSpecies,
    retry: 1,
  });

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading species...</div>;
  }

  if (error) {
    console.error('Error in SpeciesPanel:', error);
    return <div className="text-sm text-red-500">Failed to load species</div>;
  }

  if (!species || !Array.isArray(species) || species.length === 0) {
    return <div className="text-sm text-gray-500">No species available</div>;
  }

  // Show first 3 species for featured section
  const featuredSpecies = species.slice(0, 3);

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-gray-700">Featured Species</div>
      
      {featuredSpecies.map((spec: TreeSpecies) => (
        <div key={spec.id} className="card p-3">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-forest-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-forest-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h5 className="font-medium text-gray-900">{spec.name}</h5>
              <p className="text-sm text-gray-500 italic">{spec.scientific_name}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const SpeciesPanel: React.FC = () => {
  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Tree Species</h3>
        <p className="text-sm text-gray-600">
          Browse and learn about tree species suitable for urban forestry in the Hackensack area.
        </p>
      </div>

      <div className="space-y-4">
        {/* Search and Filter */}
        <div>
          <input
            type="text"
            placeholder="Search species..."
            className="input-field"
          />
        </div>

        {/* Quick Filters */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Filters</h4>
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200">
              Native
            </button>
            <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200">
              Fast Growing
            </button>
            <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200">
              Drought Tolerant
            </button>
            <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200">
              Low Maintenance
            </button>
          </div>
        </div>

        {/* Species List */}
        <SpeciesList />

        <div className="pt-4 border-t border-gray-200">
          <button className="btn-primary w-full">
            View All Species
          </button>
        </div>
      </div>
    </div>
  );
};
